import React, { useRef, useState } from 'react';
import { Button, ButtonGroup } from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { lazy } from '@loadable/component';
import { isEmptyMessage } from 'utils/editor';
import { graphql, useFragment, useMutation } from 'utils/graphClient';
import useSubmitMessage from 'hooks/useSubmitMessage';
import { EditAnnouncementMessage_chat_updateMessage_Mutation } from './__generated__/EditAnnouncementMessage_chat_updateMessage_Mutation.graphql';
import { EditAnnouncementMessage_chat_messageFragment$key } from './__generated__/EditAnnouncementMessage_chat_messageFragment.graphql';

const ChatEditor = lazy(() => import('components/Editor/ChatEditor'));

const messageFragment = graphql`
  fragment EditAnnouncementMessage_chat_messageFragment on Message {
    id
    text
    updatedAt
    deletedAt
    isEdited
    conversation {
      id
      participants
      reference {
        internal_id
      }
    }
  }
`;

const updateMessageMutation = graphql`
  mutation EditAnnouncementMessage_chat_updateMessage_Mutation(
    $id: ID!
    $text: JSON!
  ) {
    updateMessage(input: { messageId: $id, text: $text }) {
      ...EditMessage_chat_messageFragment
    }
  }
`;

interface EditMessageProps {
  message: EditAnnouncementMessage_chat_messageFragment$key;
  setIsEditingMessage: (isEditingMessage: boolean) => void;
}

export const EditAnnouncementMessage: React.FC<EditMessageProps> = ({
  message,
  setIsEditingMessage,
}) => {
  const { id, text, conversation } = useFragment(messageFragment, message);

  const [updateMessage, updateMessageLoading] =
    useMutation<EditAnnouncementMessage_chat_updateMessage_Mutation>(
      updateMessageMutation,
    );

  const [editorKey, setEditorKey] = useState(0);
  const [content, setContent] = useState<any>(undefined);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const messageChangeHandler = (stringifiedMessage?: string) => {
    const message = JSON.parse(stringifiedMessage || '');

    if (isEmptyMessage(message)) {
      setContent(undefined);
    } else {
      setContent(message);
    }
  };

  const { triggerSubmit } = useSubmitMessage(() => {
    if (!isEmptyMessage(content)) {
      updateMessage({
        variables: {
          id,
          text: JSON.stringify(content),
        },
        onCompleted: () => setIsEditingMessage(false),
      });
    }
  });

  return (
    <>
      <ChatEditor
        key={editorKey}
        onChange={messageChangeHandler}
        initialData={typeof text === 'string' ? JSON.parse(text) : text}
        sendMessage={triggerSubmit}
        participantIds={conversation.participants as string[]}
        spaceId={conversation.reference.internal_id}
      />
      <ButtonGroup pt={2} justifyContent="flex-end" size="sm">
        <Button
          size="sm"
          onClick={() => {
            setEditorKey(editorKey + 1);
            setIsEditingMessage(false);
          }}
        >{t`Cancel`}</Button>
        <Button
          colorScheme="blue"
          size="sm"
          isLoading={updateMessageLoading}
          isDisabled={!content}
          ref={buttonRef}
          onClick={() => {
            updateMessage({
              variables: {
                id,
                text: JSON.stringify(content),
              },
              onCompleted: () => setIsEditingMessage(false),
            });
          }}
        >{t`Save`}</Button>
      </ButtonGroup>
    </>
  );
};
